import React from 'react';
import { ColumnDef } from '../companies.config';
import { SelectOption } from '../simulatorSettings.constants';
import './EditableTable.css';

export interface CompanyOption {
    value: string;
    label: string;
}

interface EditableTableProps {
    title: string;
    columns: ColumnDef[];
    rows: any[];
    companyOptions: CompanyOption[];
    /** option sets for 'dynamicSelect' columns, keyed by ColumnDef.optionsKey */
    dynamicOptions?: Record<string, CompanyOption[]>;
    saving?: boolean;
    feedback?: { ok: boolean; msg: string } | null;
    /** optional extra per-row button (e.g. "תנאים וכיסויים"), rendered before delete */
    rowActionLabel?: string;
    rowActionTitle?: string;
    onRowAction?: (index: number) => void;
    /** when returns true, the row-action button shows a "has content" marker */
    rowActionActive?: (row: any) => boolean;
    onChange: (rows: any[]) => void;
    onAdd: () => void;
    onDelete: (index: number) => void;
    onSave: () => void;
}

/**
 * Generic editable grid for the simulator settings tables (routes / agents).
 * Each column declares how to read/write its value off a row, so the same
 * component drives every company by swapping the column config.
 */
const EditableTable: React.FC<EditableTableProps> = ({
    title,
    columns,
    rows,
    companyOptions,
    dynamicOptions,
    saving,
    feedback,
    rowActionLabel,
    rowActionTitle,
    onRowAction,
    rowActionActive,
    onChange,
    onAdd,
    onDelete,
    onSave,
}) => {
    const update = (rowIndex: number, col: ColumnDef, value: any) => {
        const next = rows.slice();
        if (col.type === 'radio' && value === 'checked') {
            // single-select across rows: only the chosen row stays checked
            next.forEach((r, i) => col.set(r, i === rowIndex ? 'checked' : ''));
        } else {
            col.set(next[rowIndex], value);
        }
        onChange(next);
    };

    const renderCell = (row: any, rowIndex: number, col: ColumnDef) => {
        const value = col.get(row);

        if (col.type === 'company' || col.type === 'dynamicSelect') {
            const opts =
                col.type === 'company'
                    ? companyOptions
                    : dynamicOptions?.[col.optionsKey ?? ''] ?? [];
            return (
                <select
                    className="sbsim-table__select"
                    value={(value ?? '').toString().toLowerCase()}
                    onChange={(e) => update(rowIndex, col, e.target.value)}
                >
                    {col.type === 'dynamicSelect' && <option value="" />}
                    {opts.map((o) => (
                        <option key={o.value} value={o.value.toLowerCase()}>
                            {o.label}
                        </option>
                    ))}
                </select>
            );
        }

        if (col.type === 'radio') {
            return (
                <input
                    type="radio"
                    className="sbsim-table__radio"
                    name={`sbsim-radio-${col.key}`}
                    checked={value === 'checked'}
                    onChange={() => update(rowIndex, col, 'checked')}
                />
            );
        }

        if (col.type === 'status' || col.type === 'select') {
            const options: SelectOption[] = col.options ?? [];
            return (
                <select
                    className="sbsim-table__select"
                    value={value ?? ''}
                    onChange={(e) => update(rowIndex, col, e.target.value)}
                >
                    {options.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            );
        }

        return (
            <input
                className="sbsim-table__input"
                type={col.type === 'number' ? 'number' : 'text'}
                value={value ?? ''}
                placeholder={col.placeholder}
                onChange={(e) => update(rowIndex, col, e.target.value)}
            />
        );
    };

    return (
        <div className="sbsim-table">
            <div className="sbsim-table__title">{title}</div>
            <div className="sbsim-table__scroll">
                <table className="sbsim-table__grid">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key}>{c.label}</th>
                            ))}
                            <th className="sbsim-table__actions-col" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td className="sbsim-table__empty" colSpan={columns.length + 1}>
                                    אין נתונים
                                </td>
                            </tr>
                        )}
                        {rows.map((row, rowIndex) => (
                            <tr key={row.id ?? `new-${rowIndex}`}>
                                {columns.map((col) => (
                                    <td key={col.key}>{renderCell(row, rowIndex, col)}</td>
                                ))}
                                <td className="sbsim-table__actions">
                                    {onRowAction && (
                                        <button
                                            type="button"
                                            className={`sbsim-table__row-action${
                                                rowActionActive?.(row)
                                                    ? ' sbsim-table__row-action--active'
                                                    : ''
                                            }`}
                                            title={
                                                rowActionActive?.(row)
                                                    ? `${rowActionTitle ?? ''} (קיים תוכן)`
                                                    : rowActionTitle
                                            }
                                            onClick={() => onRowAction(rowIndex)}
                                        >
                                            {rowActionActive?.(row) && (
                                                <span
                                                    className="sbsim-table__row-action-dot"
                                                    aria-hidden="true"
                                                />
                                            )}
                                            {rowActionLabel}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="sbsim-table__delete"
                                        title="מחק"
                                        onClick={() => onDelete(rowIndex)}
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="sbsim-table__footer">
                <button type="button" className="sbsim-table__add" onClick={onAdd}>
                    + הוסף
                </button>
                <button
                    type="button"
                    className="sbsim-table__save"
                    onClick={onSave}
                    disabled={saving}
                >
                    {saving ? 'שומר...' : 'שמור הכל'}
                </button>
                {feedback && (
                    <span
                        className={`sbsim-table__feedback ${
                            feedback.ok
                                ? 'sbsim-table__feedback--ok'
                                : 'sbsim-table__feedback--err'
                        }`}
                    >
                        {feedback.msg}
                    </span>
                )}
            </div>
        </div>
    );
};

export default EditableTable;
